import { apiStages } from "@/api/recruit/stages.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException } from "@/types/api.types.ts";
import { ApiPipelineStage, ApiPipelineStagSavePayload } from "@/types/recruit/stages.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Control, FieldValues } from "react-hook-form";

const stagesKeys = createQueryKeys("stages", {
  all: null,
});

export const useStages = (enabled: boolean = true) => {
  return useQuery({
    ...stagesKeys.all,
    queryFn: async () => {
      const result = await apiStages.getAll();
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    staleTime: 10 * 60 * 1000,
    enabled: enabled,
  });
};

export interface SaveParams {
  id: number;
  payload: ApiPipelineStagSavePayload;
}

export const useStageSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async ({ id, payload }: SaveParams) => {
      const result =
        id == 0 ? await apiStages.create(payload) : await apiStages.update(id, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },

    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: stagesKeys.all.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useStageDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (id: number) => {
      const result = await apiStages.delete(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: stagesKeys.all.queryKey,
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useStageSort = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();
  return useMutation({
    mutationFn: async (ids: number[]) => {
      queryClient.setQueryData(stagesKeys.all.queryKey, (old: ApiPipelineStage[]) => {
        const sorted = ids.map((id: number) => old.find((d: ApiPipelineStage) => d.id === id));
        return sorted;
      });

      const result = await apiStages.updateSort({ ids });
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },

    // onSuccess: () => {
    // queryClient.invalidateQueries({
    //   queryKey: stagesKeys.all.queryKey,
    // });
    // },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
